/**
 * The overlay provider is used to manage screen overlays such as Flyouts or modals, which are use to display content in a dynamic fashion based on the directive from the amplience request
 *
 * @module views/components/OverlayProvider
 * @memberof -Common
 */
import React, {
  useState,
  useRef,
  useContext,
  createContext,
  useEffect
} from 'react';
import OverlayModal from '../OverlayModal/OverlayModal';
import OverlayFlyout from '../OverlayFlyout/OverlayFlyout';
import AsyncComponent from '../AsyncComponent/AsyncComponent';
import { isServer } from '../../../utils/device_detection/device_detection';
import './OverlayProvider.scss';

/**
 * Returns whether the given type is a valid overlay type.
 * @param {string} type
 * @returns True if a valid overlay type, otherwise false.
 */
export const isOverlay = ( type ) => {
  return type === 'modal' || type === 'flyout';
};

import { getDialogPolyfill } from './getDialogPolyfill';
/**
 * Represents a OverlayProvider component
 *
 * @method
 * @param {object} props - React properties passed from composition
 * @returns OverlayProvider
 */
export const OverlayProvider = function( props ){
  const [displayOverlay, setDisplayOverlay] = useState( false );
  const [overlayRef, setOverlayRef] = useState( useRef() );
  const [overlayType, setOverlayType] = useState( undefined );
  const [overlayOpener, setOverlayOpener] = useState();
  const [overlayContent, setOverlayContent] = useState();
  const [overlayAlignment, setOverlayAlignment] = useState( 'center' );
  const [overlayCloseAccessibilityAction, setOverlayCloseAccessibilityAction] =
    useState();
  const [isVideo, setVideo] = useState( false );
  const [overlayOnClose, setOverlayOnClose] = useState();
  const [videoMeta, setVideoMeta] = useState();
  const [isSwatchModal, setSwatchModal] = useState( false );
  const [crossButtonVisibility, setCrossButtonVisibility] = useState( true );
  const [customClassName, setCustomClassName] = useState( '' );

  useEffect( () => {
    if(
      !isServer() &&
      displayOverlay &&
      typeof HTMLDialogElement !== 'function'
    ){
      getDialogPolyfill().then( ( registerDialog ) => {
        registerDialog( overlayRef.current );
        overlayRef?.current?.showModal();
      } );
    }

    if( overlayRef.current ){
      overlayRef.current.onclose = ( e ) => {
        e.stopPropagation();
        overlayOpener.current?.focus();
        closeOverlay();
      };
    }
  }, [displayOverlay, overlayRef.current] );

  useEffect( () => {
    const scrollBlockClassName = 'Overlay-Scroll-Block';
    if( displayOverlay ){
      // Setting the current scroll posoiton to body
      global.document.body.style.top = `-${global.scrollY}px`;
      // if the overlay is open then block page scrolling
      global.document.body.classList.add( scrollBlockClassName );
      overlayRef?.current?.showModal?.();
    }
    else {
      const scrollY = global.document.body.style.top;
      global.document.body.classList.remove( scrollBlockClassName );
      global.document.body.style.top = '';
      // Restoring the scroll positon which was set earlier
      global.scrollTo( 0, parseInt( scrollY || '0', 10 ) * -1 );
    }
  }, [displayOverlay, overlayRef] );

  /**
   * creating custom event listener for closing the modal to support in safari browswer
   */
  const handleModalClick = ( e ) => {
    e.stopPropagation();

    if( e.target.tagName !== 'DIALOG' ){
      return;
    }
    const rect = e.target.getBoundingClientRect();

    const clickedInDialog =
      rect.top <= e.clientY &&
      e.clientY <= rect.top + rect.height &&
      rect.left <= e.clientX &&
      e.clientX <= rect.left + rect.width;

    if( clickedInDialog === false ){
      e.target.close();
    }
  };

  const openOverlay = ( setOverlayOpener ) => {
    return ( {
      type,
      opener,
      content,
      isVideo,
      videoMeta,
      alignment,
      closeAccessibilityAction,
      onClose,
      graphql,
      config,
      isSwatchModal,
      crossButtonVisibility,
      customClassName
    } ) => {
      setOverlayAlignment( alignment );
      setOverlayOpener( opener );
      setDisplayOverlay( true );
      if( content ){
        setOverlayContent( content );
      }
      else {
        setOverlayContent(
          <AsyncComponent
            meta={ {
              /* pass the query as the initAction make sure the query invokves itself */
              initAction: { graphql, variables: config.variables }
            } }
          />
        );
      }
      setCustomClassName( customClassName );
      setCrossButtonVisibility( crossButtonVisibility );
      setOverlayType( type );
      setVideo( isVideo );
      setVideoMeta( videoMeta );
      setOverlayCloseAccessibilityAction( closeAccessibilityAction );
      setOverlayOnClose( onClose );
      setSwatchModal( isSwatchModal );
      document.addEventListener( 'click', handleModalClick );
      document.addEventListener( OVERLAY_CLOSE_EVENT, closeOverlay );
    };
  };

  const closeOverlay = () => {
    document.removeEventListener( 'click', handleModalClick );
    document.removeEventListener( OVERLAY_CLOSE_EVENT, closeOverlay );
    if( overlayOnClose ){
      overlayOnClose();
    }
    setOverlayOpener( undefined );
    setDisplayOverlay( false );
    setOverlayContent( undefined );
    setOverlayType( undefined );
    setVideo( false );
    setVideoMeta( undefined );
    setSwatchModal( false );
    setOverlayOnClose( undefined );
    setOverlayCloseAccessibilityAction( undefined );
  };

  const displayModal = displayOverlay && overlayType === 'modal';
  const displayFlyout = displayOverlay && overlayType === 'flyout';

  return (
    <OverlayContext.Provider
      value={ {
        openOverlay: openOverlay( setOverlayOpener ),
        closeOverlay,
        isOverlay
      } }
    >
      { props.children }
      { displayModal && (
        <OverlayModal
          ref={ overlayRef }
          alignment={ overlayAlignment }
          closeAccessibilityAction={ overlayCloseAccessibilityAction }
          isVideoModal={ isVideo }
          videoMeta={ videoMeta }
          isSwatchModal={ isSwatchModal }
          customClassName={ customClassName }
        >
          { overlayContent }
        </OverlayModal>
      ) }
      { displayFlyout && (
        <OverlayFlyout
          ref={ overlayRef }
          alignment={ overlayAlignment }
          closeAccessibilityAction={ overlayCloseAccessibilityAction }
          crossButtonVisibility={ crossButtonVisibility }
          customClassName={ customClassName }
        >
          { overlayContent }
        </OverlayFlyout>
      ) }
    </OverlayContext.Provider>
  );
};

export const OVERLAY_CLOSE_EVENT = 'OVERLAY_CLOSE_EVENT';

export const closeExistingOverlay = () => {
  const OverlayCloseEvent = new Event( OVERLAY_CLOSE_EVENT );
  document.dispatchEvent( OverlayCloseEvent );
};
export const OverlayContext = createContext( {} );

export const useOverlay = () => useContext( OverlayContext );

export default OverlayProvider;
